import type { ReactNode } from 'react';
import { useWhoami } from '../api/whoami';
import { NotFoundPage } from '../pages/NotFoundPage';

interface RequireModuleProps {
  /** The module whose enablement gates the route. */
  module: 'insights' | 'requests';
  children: ReactNode;
}

/**
 * Renders its children only when the required module is enabled for the
 * effective customer (`whoami.moduleSettings`). When the module is disabled it
 * renders the **404 Not Found page** rather than an explanatory error — this
 * *masks the module's existence* (WEB PRD §2.2, architecture §5.7.1). Disabled
 * modules are also hidden from the nav, so this branch is only reached via a
 * direct URL.
 *
 * When there is no module-settings block (a bare SiteAdministrator with no
 * customer scope) the module cannot be determined to be off, so access is
 * allowed. While `whoami` is loading it renders nothing.
 */
export function RequireModule({ module, children }: RequireModuleProps) {
  const { data, isPending } = useWhoami();

  if (isPending || !data) {
    return null;
  }

  const settings = data.moduleSettings;
  const enabled = !settings
    ? true
    : module === 'insights'
      ? settings.isInsightsEnabled
      : settings.isRequestsEnabled;

  return enabled ? <>{children}</> : <NotFoundPage />;
}
